import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["datepicker", "value", "defaultValue", "onChange", "id", "inputDelay", "onKeyDown"];
import PropTypes from 'prop-types';
import React, { cloneElement, forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DATE_PICKER_CHANGE_REASON, KEY_CODES } from '../../constants';
import { DatePicker } from '../DatePicker/DatePicker';
import { Dropdown } from '../Dropdown/Dropdown';
import { DropdownPopper } from '../DropdownPopper/DropdownPopper';
import { FormFieldContext } from '../FormField/FormField.context';
import { Input } from '../Input/Input';

/**
 * A date picker input is an Input field with a DatePicker as a Dropdown. You can compose this
 * component as you would a normal Input (ex: inside of an AdornedInput).
 *
 * **Note:** The date picker input does not validate values. Values selected from the date picker
 * dropdown will follow the `dateFormat` provided to DatePicker, but the input
 * itself does not prevent the user from entering invalid values. You can use the
 * [date-nfs](https://date-fns.org/) library for any validation logic.
 *
 * @related [`DatePicker`]
 * @since 8.0.0
 */
export var DatePickerInput = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var datepicker = _ref.datepicker,
    controlledValue = _ref.value,
    defaultValue = _ref.defaultValue,
    _onChange = _ref.onChange,
    id = _ref.id,
    inputDelay = _ref.inputDelay,
    onKeyDown = _ref.onKeyDown,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(defaultValue),
    _useState2 = _slicedToArray(_useState, 2),
    uncontrolledValue = _useState2[0],
    setUncontrolledValue = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isDropdownOpen = _useState4[0],
    setIsDropdownOpen = _useState4[1];
  var value = typeof controlledValue !== 'undefined' ? controlledValue : uncontrolledValue;
  var _useState5 = useState(value),
    _useState6 = _slicedToArray(_useState5, 2),
    calendarValue = _useState6[0],
    setCalendarValue = _useState6[1];
  var context = useContext(FormFieldContext);
  var isReadOnly = typeof rest.readOnly !== 'undefined' ? rest.readOnly : context.readOnly;
  var calendarRef = useRef();
  var innerRef = useRef();
  var inputRef = forwardedRef || innerRef;
  var inputTimeout = useRef();
  useEffect(function () {
    setCalendarValue(value);
  }, [value]);
  var onInputChangeCallback = useCallback(function (e) {
    var changed = e.target.value;
    setUncontrolledValue(changed);
    if (_onChange) {
      _onChange(changed, {
        reason: DATE_PICKER_CHANGE_REASON.MANUAL_INPUT
      });
    }
    // create a debounce so calendar could delay its response
    // to input change
    clearTimeout(inputTimeout.current);
    inputTimeout.current = setTimeout(function () {
      setCalendarValue(changed);
    }, inputDelay);
  }, [inputDelay, _onChange]);
  var onOpenCallback = useCallback(function () {
    setIsDropdownOpen(true);
  }, []);
  var onCloseCallback = useCallback(function () {
    setIsDropdownOpen(false);
  }, []);
  var onKeyDownCallback = useCallback(function (e) {
    // Close dropdown on tab
    if (e.keyCode === KEY_CODES.TAB) {
      setIsDropdownOpen(false);
    }

    // Open the dropdown and prevent up/down from scrolling the page
    if (e.keyCode === KEY_CODES.ARROW_DOWN || e.keyCode === KEY_CODES.ARROW_UP) {
      setIsDropdownOpen(true);
      if (calendarRef && calendarRef.current) {
        calendarRef.current.focus();
      }
      e.preventDefault();
    }

    // Ignore enter if dropdown is open
    if (e.keyCode === KEY_CODES.ENTER) {
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
        e.preventDefault();
      }
    }
    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  }, [isDropdownOpen, onKeyDown]);
  var dropdown = /*#__PURE__*/React.createElement(Dropdown, {
    shouldIgnoreTriggerClick: true,
    shouldReturnFocus: false,
    onESCKeyPress: function onESCKeyPress() {
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, useCallback(function (_ref2) {
    var onClose = _ref2.onClose;
    return /*#__PURE__*/cloneElement(datepicker, {
      margin: 'sm',
      onChange: function onChange(e, meta) {
        var changed = e.formatted;
        setUncontrolledValue(changed);
        setCalendarValue(changed);
        if (_onChange) {
          _onChange(changed, meta);
        }
        if (inputRef && inputRef.current) {
          inputRef.current.focus();
        }
        onClose();
      },
      selected: calendarValue,
      ref: calendarRef
    });
  }, [calendarValue, datepicker, inputRef, _onChange]));
  return /*#__PURE__*/React.createElement(DropdownPopper, {
    id: id,
    shouldFocusTriggered: false,
    triggered: dropdown,
    openWith: isReadOnly ? [] : ['click', 'focus'],
    onClose: onCloseCallback,
    onOpen: onOpenCallback,
    isOpen: isDropdownOpen,
    role: "combobox"
  }, /*#__PURE__*/React.createElement(Input, _extends({
    ref: inputRef,
    value: value || '',
    onChange: onInputChangeCallback,
    onKeyDown: onKeyDownCallback
  }, rest)));
});
DatePickerInput.displayName = 'DatePickerInput';
DatePickerInput.propTypes = process.env.NODE_ENV !== "production" ? {
  'aria-autocomplete': PropTypes.string,
  'aria-label': PropTypes.string,
  'id': PropTypes.string,
  /**
   * You can customize the `DatePicker` by providing your own component.
   */
  'datepicker': PropTypes.node,
  /**
   * The value when using this as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   */
  'value': PropTypes.string,
  /**
   * The default value when using this as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  'defaultValue': PropTypes.string,
  /**
   * Callback function when a day is selected from the calendar. It will receive a formatted date
   * and a meta object.
   *
   * **Note:** The value is not validated. Values selected from the date picker
   * dropdown will follow the `dateFormat` provided to DatePicker, but the input
   * itself does not prevent the user from entering invalid values. You can use the
   * [date-nfs](https://date-fns.org/) library for any validation logic.
   */
  'onChange': PropTypes.func,
  /**
   * Displays the input in a read-only state. Can also be inherited from a FormField parent.
   *
   * @since 8.31.0
   */
  'readOnly': PropTypes.bool,
  /**
   * This is the delay in milliseconds between keystrokes after which the
   * `DatePicker` will receive the input value and display the date in the calendar.
   *
   * @since 8.31.0
   */
  'inputDelay': PropTypes.number,
  /**
   * @ignore
   */
  'onKeyDown': PropTypes.func
} : {};
DatePickerInput.defaultProps = {
  'aria-autocomplete': 'none',
  'aria-label': 'Choose Date',
  'datepicker': /*#__PURE__*/React.createElement(DatePicker, null),
  'inputDelay': 500
};